// apps/app/src/components/builder/recommendCharts.ts
/* eslint-disable @typescript-eslint/no-non-null-assertion */

export type ColumnType = "numeric" | "categorical" | "datetime" | "unknown"

export type ColumnMeta = {
  key: string
  label?: string
  type?: ColumnType
  // Estimated unique count. If missing, we only use heuristics.
  cardinality?: number
  // Optional hint if your fingerprint provides it
  role_guess?: ColumnType
}

export type Fingerprint = {
  rows?: number
  columns: ColumnMeta[]
  suggestions?: {
    plotTypes?: string[]
  }
}

export type RoleState = {
  x?: string | null
  // Support both old single-Y and new multi-Y shapes
  y?: string | string[] | null
  series?: string | null
  facet?: string | null
  uncertainty?: string | null
  n?: string | null
}

export type RecommendStage = "preRoles" | "xOnly" | "xy" | "grouped"

export type RecommendResult = {
  stage: RecommendStage
  recommendedIds: string[]
  reasonsByChartId: Record<string, string[]>
  // For UI rendering in preRoles state
  hint?: string
}

type ChartStructure =
  | "line_single"
  | "line_multi"
  | "bar_single"
  | "bar_grouped"
  | "bar_stacked"
  | "area_stacked"
  | "scatter_raw"
  | "scatter_agg"
  | "distribution_1d"
  | "distribution_grouped"
  | "matrix_density"
  | "special"
  | "other"

type ChartFamily =
  | "trend"
  | "compare"
  | "relationship"
  | "distribution"
  | "special"
  | "other"

type ChartCaps = {
  id: string
  requiresX: boolean
  requiresY: boolean

  // Role compatibility
  supportsSeries: boolean
  supportsFacet: boolean
  supportsMultiY: boolean

  // Preferred X/Y types (soft, used in scoring)
  prefersX?: ColumnType[]
  prefersY?: ColumnType[]

  // Chart semantics
  family: ChartFamily
  structure: ChartStructure

  // Base bias
  baseWeight: number

  // Used to hard-exclude rare charts from recommendations
  excludeFromRecommendations?: boolean

  // Allow axis-flip for type preference checks (filtering only, not UI swapping)
  // When true and xType==="numeric" && yType==="categorical", check prefersX/prefersY with swapped types
  allowPrefTypeFlip?: boolean

  // Support implicit count Y in xOnly stage (for categorical X frequency charts)
  // When true, chart can be recommended in xOnly with categorical X by implicitly counting rows per category
  supportsImplicitCountY?: boolean
}

// Hard exclusions from the recommended shortlist (still available via full catalog)
const EXCLUDED_RECOMMENDATION_CHARTS = new Set<string>([
  "lollipop_horizontal",
  "lollipop_vertical",
  "dumbbell",
])

// Charts you listed. We keep the catalog list external, and only recommend from these ids.
export const ALL_CHART_IDS: string[] = [
  "area_stacked",
  "bar_horizontal_stacked",
  "bar_vertical",
  "bar_vertical_grouped",
  "bar_vertical_stacked",
  "bar_vertical_sorted",
  "boxplot",
  "bullet_graph",
  "candlestick",
  "cluster_scatter",
  "connected_scatter",
  "dendrogram",
  "density_curve_kde",
  "donut",
  "dumbbell",
  "chord",
  "funnel",
  "gantt",
  "heatmap",
  "histogram",
  "line_bar_combo",
  "line",
  "line_ranked_bump",
  "line_stepped",
  "logistic_regression_curve",
  "lollipop_horizontal",
  "lollipop_vertical",
  "pie",
  "polar_area",
  "population_pyramid",
  "proportional_circles",
  "radar",
  "sankey",
  "scatter_bubble",
  "scatter",
  "slope_two_point",
  "treemap",
  "violin",
  "waterfall",
  "diverging_bar_single_value_per_row",
  "diverging_stacked_bar_segment_counts",
  "diverging_100_stacked_bar_likert_percent",
  "ridgeline",
  "hexbin_chart",
  "forest",
  "volcano_plot",
  "geo_map",
]

// Minimal capability map. It does not need to be perfect to work well.
// It must be good for the common charts so recommendations are stable and sane.
const CHART_CAPS: Record<string, ChartCaps> = {
  // Trend
  line: {
    id: "line",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: true,
    family: "trend",
    structure: "line_multi",
    baseWeight: 1.2,
    prefersX: ["datetime", "categorical", "numeric", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  line_stepped: {
    id: "line_stepped",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: true,
    family: "trend",
    structure: "line_multi",
    baseWeight: 1.05,
    prefersX: ["datetime", "categorical", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  line_ranked_bump: {
    id: "line_ranked_bump",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: false,
    family: "trend",
    structure: "line_multi",
    baseWeight: 0.95,
    prefersX: ["datetime", "categorical", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  area_stacked: {
    id: "area_stacked",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: true,
    family: "trend",
    structure: "area_stacked",
    baseWeight: 0.95,
    prefersX: ["datetime", "categorical", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  line_bar_combo: {
    id: "line_bar_combo",
    requiresX: true,
    requiresY: true,
    supportsSeries: false,
    supportsFacet: true,
    supportsMultiY: true,
    family: "trend",
    structure: "other",
    baseWeight: 0.9,
    prefersX: ["datetime", "categorical", "unknown"],
    prefersY: ["numeric", "unknown"],
  },

  // Compare
  bar_vertical: {
    id: "bar_vertical",
    requiresX: true,
    requiresY: true,
    supportsSeries: false,
    supportsFacet: true,
    supportsMultiY: false, // Single bar chart - use grouped for multi-Y
    family: "compare",
    structure: "bar_single",
    baseWeight: 1.15,
    prefersX: ["categorical", "datetime", "unknown"],
    prefersY: ["numeric", "unknown"],
    supportsImplicitCountY: true,
  },
  bar_vertical_sorted: {
    id: "bar_vertical_sorted",
    requiresX: true,
    requiresY: true,
    supportsSeries: false,
    supportsFacet: true,
    supportsMultiY: false, // Single bar chart - use grouped for multi-Y
    family: "compare",
    structure: "bar_single",
    baseWeight: 1.05,
    prefersX: ["categorical", "unknown"],
    prefersY: ["numeric", "unknown"],
    supportsImplicitCountY: true,
  },
  bar_vertical_grouped: {
    id: "bar_vertical_grouped",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: true,
    family: "compare",
    structure: "bar_grouped",
    baseWeight: 1.15,
    prefersX: ["categorical", "datetime", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  bar_vertical_stacked: {
    id: "bar_vertical_stacked",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: true,
    family: "compare",
    structure: "bar_stacked",
    baseWeight: 0.95,
    prefersX: ["categorical", "datetime", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  bar_horizontal_stacked: {
    id: "bar_horizontal_stacked",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: true,
    family: "compare",
    structure: "bar_stacked",
    baseWeight: 0.9,
    prefersX: ["categorical", "unknown"],
    prefersY: ["numeric", "unknown"],
    allowPrefTypeFlip: true,
  },
  bullet_graph: {
    id: "bullet_graph",
    requiresX: true,
    requiresY: true,
    supportsSeries: false,
    supportsFacet: true,
    supportsMultiY: true,
    family: "compare",
    structure: "other",
    baseWeight: 0.75,
    prefersX: ["categorical", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  waterfall: {
    id: "waterfall",
    requiresX: true,
    requiresY: true,
    supportsSeries: false,
    supportsFacet: true,
    supportsMultiY: false,
    family: "compare",
    structure: "other",
    baseWeight: 0.7,
    prefersX: ["categorical", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  treemap: {
    id: "treemap",
    requiresX: true,
    requiresY: true,
    supportsSeries: false,
    supportsFacet: true,
    supportsMultiY: false,
    family: "compare",
    structure: "other",
    baseWeight: 0.85,
    prefersX: ["categorical", "unknown"],
    prefersY: ["numeric", "unknown"],
    supportsImplicitCountY: true,
  },
  donut: {
    id: "donut",
    requiresX: true,
    requiresY: true,
    supportsSeries: false,
    supportsFacet: true,
    supportsMultiY: false,
    family: "compare",
    structure: "other",
    baseWeight: 0.8,
    prefersX: ["categorical", "unknown"],
    prefersY: ["numeric", "unknown"],
    supportsImplicitCountY: true,
  },
  pie: {
    id: "pie",
    requiresX: true,
    requiresY: true,
    supportsSeries: false,
    supportsFacet: true,
    supportsMultiY: false,
    family: "compare",
    structure: "other",
    baseWeight: 0.75,
    prefersX: ["categorical", "unknown"],
    prefersY: ["numeric", "unknown"],
    supportsImplicitCountY: true,
  },

  // Distribution
  histogram: {
    id: "histogram",
    requiresX: true,
    requiresY: false,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: false,
    family: "distribution",
    structure: "distribution_1d",
    baseWeight: 1.1,
    prefersX: ["numeric", "datetime", "unknown"],
  },
  density_curve_kde: {
    id: "density_curve_kde",
    requiresX: true,
    requiresY: false,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: false,
    family: "distribution",
    structure: "distribution_1d",
    baseWeight: 0.95,
    prefersX: ["numeric", "unknown"],
  },
  ridgeline: {
    id: "ridgeline",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: false,
    family: "distribution",
    structure: "distribution_grouped",
    baseWeight: 0.75,
    prefersX: ["numeric", "unknown"],
    prefersY: ["categorical", "unknown"],
  },
  boxplot: {
    id: "boxplot",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: true, // Can show multiple Y measures as separate boxplots
    family: "distribution",
    structure: "distribution_grouped",
    baseWeight: 1.05,
    prefersX: ["categorical", "datetime", "unknown"],
    prefersY: ["numeric", "unknown"],
    allowPrefTypeFlip: true,
  },
  violin: {
    id: "violin",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: true, // Can show multiple Y measures as separate violins
    family: "distribution",
    structure: "distribution_grouped",
    baseWeight: 0.95,
    prefersX: ["categorical", "datetime", "unknown"],
    prefersY: ["numeric", "unknown"],
    allowPrefTypeFlip: true,
  },

  // Relationship
  scatter: {
    id: "scatter",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: false,
    family: "relationship",
    structure: "scatter_raw",
    baseWeight: 1.1,
    prefersX: ["numeric", "datetime", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  connected_scatter: {
    id: "connected_scatter",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: false,
    family: "relationship",
    structure: "scatter_raw",
    baseWeight: 0.85,
    prefersX: ["numeric", "datetime", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  cluster_scatter: {
    id: "cluster_scatter",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: false,
    family: "relationship",
    structure: "scatter_raw",
    baseWeight: 0.75,
    prefersX: ["numeric", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  scatter_bubble: {
    id: "scatter_bubble",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: false,
    family: "relationship",
    structure: "scatter_raw",
    baseWeight: 0.85,
    prefersX: ["numeric", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  hexbin_chart: {
    id: "hexbin_chart",
    requiresX: true,
    requiresY: true,
    supportsSeries: false,
    supportsFacet: true,
    supportsMultiY: false,
    family: "relationship",
    structure: "scatter_agg",
    baseWeight: 1.05,
    prefersX: ["numeric", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  heatmap: {
    id: "heatmap",
    requiresX: true,
    requiresY: true,
    supportsSeries: false,
    supportsFacet: true,
    supportsMultiY: true,
    family: "relationship",
    structure: "matrix_density",
    baseWeight: 0.95,
    prefersX: ["categorical", "numeric", "unknown"],
    prefersY: ["categorical", "numeric", "unknown"],
  },

  // Special
  geo_map: {
    id: "geo_map",
    requiresX: true,
    requiresY: false,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: false,
    family: "special",
    structure: "special",
    baseWeight: 0.6,
  },
  candlestick: {
    id: "candlestick",
    requiresX: true,
    requiresY: true,
    supportsSeries: false,
    supportsFacet: true,
    supportsMultiY: false,
    family: "special",
    structure: "special",
    baseWeight: 0.65,
    prefersX: ["datetime", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  volcano_plot: {
    id: "volcano_plot",
    requiresX: true,
    requiresY: true,
    supportsSeries: false,
    supportsFacet: true,
    supportsMultiY: false,
    family: "special",
    structure: "special",
    baseWeight: 0.55,
    prefersX: ["numeric", "unknown"],
    prefersY: ["numeric", "unknown"],
  },
  forest: {
    id: "forest",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: false,
    family: "special",
    structure: "special",
    baseWeight: 0.45,
  },

  // Charts we intentionally do not recommend (hard exclusion)
  lollipop_horizontal: {
    id: "lollipop_horizontal",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: true,
    family: "compare",
    structure: "other",
    baseWeight: 0.0,
    excludeFromRecommendations: true,
  },
  lollipop_vertical: {
    id: "lollipop_vertical",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: true,
    family: "compare",
    structure: "other",
    baseWeight: 0.0,
    excludeFromRecommendations: true,
  },
  dumbbell: {
    id: "dumbbell",
    requiresX: true,
    requiresY: true,
    supportsSeries: true,
    supportsFacet: true,
    supportsMultiY: false,
    family: "compare",
    structure: "other",
    baseWeight: 0.0,
    excludeFromRecommendations: true,
  },
}

// Default caps for charts not listed above. They will rarely win, but can still be recommended if compatible.
function getCaps(chartId: string): ChartCaps {
  const caps = CHART_CAPS[chartId]
  if (caps) return caps
  return {
    id: chartId,
    requiresX: true,
    requiresY: true,
    supportsSeries: false,
    supportsFacet: true,
    supportsMultiY: false,
    family: "other",
    structure: "other",
    baseWeight: 0.35,
  }
}

function asArrayY(y: RoleState["y"]): string[] {
  if (!y) return []
  if (Array.isArray(y)) return y.filter(Boolean) as string[]
  return [y].filter(Boolean) as string[]
}

function normalizeColName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, "_")
}

function colByKey(fp: Fingerprint | null, key: string | null | undefined): ColumnMeta | null {
  if (!fp || !key) return null
  const k = key
  return fp.columns.find(c => c.key === k) ?? null
}

function inferType(fp: Fingerprint | null, colKey: string | null | undefined): ColumnType {
  const col = colByKey(fp, colKey)
  if (!col) return "unknown"

  // Priority 1: Use fingerprint's role_guess if available (from data analysis)
  if (col.role_guess && col.role_guess !== "unknown") {
    // Normalize fingerprint types: "number" -> "numeric"
    if (col.role_guess === "number" || col.role_guess === "numeric") return "numeric"
    if (col.role_guess === "datetime" || col.role_guess === "date") return "datetime"
    if (col.role_guess === "categorical" || col.role_guess === "category") return "categorical"
    return col.role_guess as ColumnType
  }

  // Priority 2: Use fingerprint's type field (from actual data analysis)
  if (col.type) {
    // Normalize fingerprint types: "number" -> "numeric", "categorical" -> "categorical"
    const normalized = col.type.toLowerCase()
    if (normalized === "number" || normalized === "numeric" || normalized === "integer" || normalized === "float") {
      return "numeric"
    }
    if (normalized === "datetime" || normalized === "date" || normalized === "time" || normalized === "timestamp") {
      return "datetime"
    }
    if (normalized === "categorical" || normalized === "category" || normalized === "string" || normalized === "text") {
      return "categorical"
    }
    if (normalized !== "unknown") {
      return col.type as ColumnType
    }
  }

  // Priority 3: Use cardinality from fingerprint data (more reliable than name heuristics)
  const rows = fp?.rows ?? 0
  const card = col.cardinality ?? 0
  if (rows > 0 && card > 0) {
    // High cardinality relative to rows suggests numeric (unique IDs are rare)
    if (card > 100 && card / rows > 0.95) {
      return "numeric"
    }
    // Low cardinality relative to rows suggests categorical
    if (card < 50 && card / rows < 0.2) {
      return "categorical"
    }
    // Medium cardinality: if very low absolute count, likely categorical
    if (card < 20) {
      return "categorical"
    }
  }

  // Priority 4: Name heuristics (last resort when fingerprint data is missing)
  const name = normalizeColName(col.label ?? col.key)
  
  // Datetime patterns
  if (/(^|_)(date|time|timestamp|datetime|year|month|day)(_|$)/.test(name)) return "datetime"

  // Numeric patterns - check before categorical to catch measure names
  if (/(^|_)(value|count|price|score|weight|height|length|amount|rate|ratio|pct|percent|mean|median|sum|total|avg)(_|$)/.test(name)) {
    return "numeric"
  }

  // ID-like categorical patterns (only if not already identified as numeric)
  if (/(^|_)(id|uuid|guid|name|category|type|status|state|country|city|region|group|class|level|tier|grade|rank|stage|phase)(_|$)/.test(name)) {
    return "categorical"
  }

  return "unknown"
}

function cardinality(fp: Fingerprint | null, colKey: string | null | undefined): number | null {
  const col = colByKey(fp, colKey)
  if (!col) return null
  return typeof col.cardinality === "number" ? col.cardinality : null
}

function detectGeo(fp: Fingerprint | null): boolean {
  if (!fp) return false
  const names = fp.columns.map(c => normalizeColName(c.label ?? c.key))
  const hasLat = names.some(n => /(lat|latitude)/.test(n))
  const hasLon = names.some(n => /(lon|lng|longitude)/.test(n))
  return hasLat && hasLon
}

function detectOHLC(fp: Fingerprint | null): boolean {
  if (!fp) return false
  const names = new Set(fp.columns.map(c => normalizeColName(c.label ?? c.key)))
  const hasO = names.has("open") || names.has("o")
  const hasH = names.has("high") || names.has("h")
  const hasL = names.has("low") || names.has("l")
  const hasC = names.has("close") || names.has("c")
  return hasO && hasH && hasL && hasC
}

function detectVolcano(fp: Fingerprint | null): boolean {
  if (!fp) return false
  const names = fp.columns.map(c => normalizeColName(c.label ?? c.key))
  const hasLogFC = names.some(n => /(logfc|log_fold_change|log2fc|log2_fold_change)/.test(n))
  const hasP = names.some(n => /(pval|p_value|pvalue|padj|fdr|qvalue|q_value|neg_log10_p|minus_log10_p)/.test(n))
  return hasLogFC && hasP
}

type Candidate = {
  id: string
  score: number
  reasons: string[]
  caps: ChartCaps
}

function stageOf(roles: RoleState): RecommendStage {
  const hasX = !!roles.x
  const yArr = asArrayY(roles.y)
  const hasY = yArr.length > 0
  const hasGrouping = !!roles.series || !!roles.facet
  if (!hasX) return "preRoles"
  if (hasX && !hasY) return "xOnly"
  if (hasX && hasY && !hasGrouping) return "xy"
  return "grouped"
}

function isCompatibleByStageAndTypes(
  caps: ChartCaps,
  stage: RecommendStage,
  xType: ColumnType,
  yType: ColumnType,
  yCount: number,
  hasSeries: boolean,
  hasFacet: boolean
): { ok: boolean; reasons: string[] } {
  const reasons: string[] = []

  if (caps.excludeFromRecommendations) return { ok: false, reasons: ["excluded_from_recommendations"] }
  if (EXCLUDED_RECOMMENDATION_CHARTS.has(caps.id)) return { ok: false, reasons: ["hard_excluded_rare_chart"] }

  // Stage gating
  if (stage === "xOnly") {
    // For xOnly, require charts that do not require Y OR support implicit count Y
    if (caps.requiresY && !caps.supportsImplicitCountY) {
      return { ok: false, reasons: ["requires_y"] }
    }
  } else if (stage === "xy" || stage === "grouped") {
    // For xy/grouped, require charts that have Y
    if (!caps.requiresY) return { ok: false, reasons: ["no_y_support"] }
  } else {
    return { ok: false, reasons: ["pre_roles_no_recs"] }
  }

  // Role compatibility
  if (yCount > 1 && !caps.supportsMultiY) return { ok: false, reasons: ["no_multi_y_support"] }
  if (hasSeries && !caps.supportsSeries) return { ok: false, reasons: ["no_series_support"] }
  if (hasFacet && !caps.supportsFacet) return { ok: false, reasons: ["no_facet_support"] }

  // Soft type compatibility: use prefers lists as a filter only when they exist and types are known
  // Be more lenient with multi-Y: if we have multiple Y columns, they're likely measures (numeric)
  // even if name heuristics suggest categorical
  
  // Special exception: allow scatter for numericX+categoricalY (strip plot use case)
  // This must be checked BEFORE type compatibility to prevent filtering out
  if (caps.id === "scatter" && xType === "numeric" && yType === "categorical") {
    // Allow scatter even if prefersY doesn't include categorical
    // Safety gate: if yCardinality is extremely high, we'll penalize later rather than filter
    reasons.push("scatter_numeric_x_categorical_y_exception")
    // Skip type compatibility checks for scatter in this pattern
  } else {
    // Check direct type preferences first
    let xTypeCompatible = true
    let yTypeCompatible = true
    
    if (caps.prefersX && xType !== "unknown" && !caps.prefersX.includes(xType)) {
      xTypeCompatible = false
    }
    if (caps.prefersY && yType !== "unknown" && !caps.prefersY.includes(yType)) {
      // For multi-Y scenarios, be more lenient: if yCount > 1, allow charts that prefer numeric
      // (multiple Y columns are typically measures, not categories)
      if (yCount > 1 && caps.prefersY.includes("numeric") && yType === "categorical") {
        // Allow this - multi-Y columns are likely numeric measures despite name heuristics
        reasons.push("multi_y_lenient_type_check")
        yTypeCompatible = true
      } else {
        yTypeCompatible = false
      }
    }
    
    // If direct check fails and we have numericX+categoricalY pattern, try axis-flip for orientation-capable charts
    if ((!xTypeCompatible || !yTypeCompatible) && xType === "numeric" && yType === "categorical" && caps.allowPrefTypeFlip) {
      // Re-check with swapped types (for filtering only, not UI swapping)
      const swappedXCompatible = caps.prefersX ? (yType === "unknown" || caps.prefersX.includes(yType)) : true
      const swappedYCompatible = caps.prefersY ? (xType === "unknown" || caps.prefersY.includes(xType)) : true
      if (swappedXCompatible && swappedYCompatible) {
        reasons.push("axis_flip_type_check_passed")
        xTypeCompatible = true
        yTypeCompatible = true
      }
    }
    
    if (!xTypeCompatible) {
      return { ok: false, reasons: ["x_type_incompatible"] }
    }
    if (!yTypeCompatible) {
      return { ok: false, reasons: ["y_type_incompatible"] }
    }
  }

  // Avoid recommending diverging charts in general, except diverging_bar_single_value_per_row
  // which we'll allow for numericX+categoricalY aggregated cases (handled in scoring/selection)
  if (caps.id.startsWith("diverging_") && caps.id !== "diverging_bar_single_value_per_row") {
    return { ok: false, reasons: ["diverging_excluded"] }
  }

  // Prefer not to recommend very niche graph/network charts by default
  if (["chord", "sankey", "dendrogram", "radar", "polar_area", "population_pyramid"].includes(caps.id)) {
    reasons.push("niche_chart_penalty_candidate")
  }

  return { ok: true, reasons }
}

function scoreCandidate(args: {
  fp: Fingerprint | null
  roles: RoleState
  stage: RecommendStage
  caps: ChartCaps
  xType: ColumnType
  yType: ColumnType
  yCount: number
  seriesCard: number
  facetCard: number
}): { score: number; reasons: string[] } {
  const { fp, roles, stage, caps, xType, yType, yCount, seriesCard, facetCard } = args

  const reasons: string[] = []
  let score = caps.baseWeight

  const rows = fp?.rows ?? 0
  const hasSeries = !!roles.series

  // Base stage adjustments
  if (stage === "xOnly") {
    // Favor common univariate charts
    if (caps.id === "histogram") score += 0.25
    if (caps.id === "density_curve_kde") score += 0.1

    // Scoring for categorical X in xOnly (implicit count Y)
    if (xType === "categorical") {
      // Primary recommendation: sorted bar chart
      if (caps.id === "bar_vertical_sorted") {
        score += 0.30
        reasons.push("boost_bar_vertical_sorted_categorical_x_only")
      }
      // Alternative: treemap for share visualization
      if (caps.id === "treemap") {
        score += 0.22
        reasons.push("boost_treemap_categorical_x_only")
      }
      // Unsorted bar chart
      if (caps.id === "bar_vertical") {
        score += 0.18
        reasons.push("boost_bar_vertical_categorical_x_only")
      }

      // Donut/pie only for low cardinality
      const xCardinality = cardinality(fp, roles.x ?? null)
      if (xCardinality !== null && xCardinality <= 8) {
        if (caps.id === "donut") {
          score += 0.10
          reasons.push("boost_donut_categorical_x_only_low_cardinality")
        }
        if (caps.id === "pie") {
          score += 0.10
          reasons.push("boost_pie_categorical_x_only_low_cardinality")
        }
      } else if (xCardinality !== null && xCardinality > 8) {
        // Penalize donut/pie for high cardinality
        if (caps.id === "donut" || caps.id === "pie") {
          score -= 1.0
          reasons.push("penalize_donut_pie_high_cardinality")
        }
      } else {
        // If cardinality is unknown, don't boost donut/pie (safer)
        if (caps.id === "donut" || caps.id === "pie") {
          score -= 0.5
          reasons.push("penalize_donut_pie_unknown_cardinality")
        }
      }

      // geo_map boost only if geo detection passed (handled separately in special-case boosts)
      // No additional boost here - let existing geo detection logic handle it
    }
  } else {
    // XY/grouped: favor common workhorses
    if (caps.id === "bar_vertical") score += 0.15
    if (caps.id === "bar_vertical_grouped") score += 0.2
    if (caps.id === "line") score += 0.25
    if (caps.id === "scatter") score += 0.2
    if (caps.id === "boxplot") score += 0.1
    if (caps.id === "violin") score += 0.05
  }

  // Prefer simpler variants when no grouping and single Y
  if (!hasSeries && yCount === 1) {
    if (caps.structure === "bar_grouped" || caps.structure === "bar_stacked" || caps.structure === "area_stacked") {
      score -= 0.55
      reasons.push("penalize_grouped_or_stacked_without_grouping")
    }
    if (caps.structure === "line_multi") {
      // line is still fine, but avoid multi-series bias when unnecessary
      score -= 0.15
      reasons.push("slight_penalty_line_multi_without_grouping")
    }
    if (caps.structure === "bar_single") {
      score += 0.15
      reasons.push("prefer_simple_bar_without_grouping")
    }
  }

  // Prefer multi-series capable charts when series or multiY present
  if ((hasSeries || yCount > 1) && stage !== "xOnly") {
    if (caps.structure === "line_multi") {
      score += 0.25
      reasons.push("boost_line_for_series_or_multiY")
    }
    if (caps.structure === "bar_grouped") {
      score += 0.2
      reasons.push("boost_grouped_bar_for_series_or_multiY")
    }
    if (caps.structure === "bar_single") {
      score -= 0.1
      reasons.push("slight_penalty_single_bar_when_grouping_present")
    }
  }

  // Type preferences
  if (xType === "datetime") {
    if (caps.family === "trend") score += 0.25
    if (caps.id === "line" || caps.id === "line_stepped") score += 0.15
    if (caps.structure === "bar_single") score += 0.05
    // Area charts work well with datetime X
    if (caps.structure === "area_stacked") score += 0.1
  }
  if (xType === "categorical") {
    if (caps.structure === "bar_single" || caps.structure === "bar_grouped") score += 0.15
    if (caps.id === "boxplot" || caps.id === "violin") score += 0.05
    // Area charts are less ideal for categorical X (better for time series)
    if (caps.structure === "area_stacked") {
      score -= 0.3
      reasons.push("penalize_area_for_categorical_x")
    }
    // Heatmap is less ideal for categorical X + numeric Y (requires aggregation)
    // Heatmaps work better for categorical×categorical or numeric×numeric
    if (caps.id === "heatmap" && yType === "numeric") {
      score -= 0.4
      reasons.push("penalize_heatmap_categorical_x_numeric_y")
    }
  }
  if (xType === "numeric" && yType === "numeric") {
    if (caps.id === "scatter") score += 0.2
    if (caps.id === "hexbin_chart") score += 0.15
    if (caps.id === "heatmap") score += 0.05
  }

  // Dedicated scoring branch for numericX+categoricalY pattern
  if (xType === "numeric" && yType === "categorical") {
    // Distribution charts: violin slightly preferred over boxplot
    if (caps.id === "violin") {
      score += 0.22
      reasons.push("boost_violin_numeric_x_categorical_y")
    }
    if (caps.id === "boxplot") {
      score += 0.20
      reasons.push("boost_boxplot_numeric_x_categorical_y")
    }
    if (caps.id === "ridgeline") {
      score += 0.18
      reasons.push("boost_ridgeline_numeric_x_categorical_y")
    }
    if (caps.id === "scatter") {
      score += 0.12
      reasons.push("boost_scatter_numeric_x_categorical_y")
    }

    // Compute heuristics for aggregated vs raw distribution
    const yCardinality = cardinality(fp, roles.y ? (Array.isArray(roles.y) ? roles.y[0] : roles.y) : null) ?? 0
    const rowsPerCategory = rows > 0 && yCardinality > 0 ? rows / Math.max(1, yCardinality) : Infinity
    const looksAggregated = rowsPerCategory <= 1.5

    if (looksAggregated) {
      // Boost aggregated comparison charts
      if (caps.id === "diverging_bar_single_value_per_row") {
        score += 0.22
        reasons.push("boost_diverging_bar_aggregated_numeric_x_categorical_y")
      }
      if (caps.id === "bullet_graph") {
        score += 0.18
        reasons.push("boost_bullet_graph_aggregated_numeric_x_categorical_y")
      }
      if (caps.id === "bar_horizontal_stacked") {
        score += 0.16
        reasons.push("boost_bar_horizontal_stacked_aggregated_numeric_x_categorical_y")
      }
    } else {
      // Raw distribution: penalize bar_horizontal_stacked so it doesn't outrank distributions
      if (caps.id === "bar_horizontal_stacked") {
        score -= 0.10
        reasons.push("penalize_bar_horizontal_stacked_raw_distribution")
      }
    }

    // Heatmap handling: penalize unless dataset is huge
    if (caps.id === "heatmap") {
      const xCardinality = cardinality(fp, roles.x ?? null) ?? 0
      const isHugeDataset = rows > 50_000 || xCardinality > 1000
      if (isHugeDataset) {
        score += 0.05
        reasons.push("small_boost_heatmap_huge_dataset")
      } else {
        // Strong penalty to ensure heatmap doesn't win by baseWeight for small/medium datasets
        score -= 0.5
        reasons.push("penalize_heatmap_small_medium_dataset_numeric_x_categorical_y")
      }
    }

    // Safety gate: if yCardinality is extremely high, penalize scatter
    if (caps.id === "scatter" && yCardinality > 60) {
      score -= 0.15
      reasons.push("penalize_scatter_high_y_cardinality")
    }
  }

  // Readability penalties for cardinality
  // Treat multi-Y as a "measure series" cardinality
  const effectiveSeriesCard = hasSeries ? seriesCard : (yCount > 1 ? yCount : 0)

  if (effectiveSeriesCard > 8) {
    if (caps.structure === "bar_grouped" || caps.structure === "bar_stacked" || caps.structure === "area_stacked") {
      score -= 0.6
      reasons.push("penalize_bars_or_stacks_high_group_count")
    }
    if (caps.structure === "line_multi") {
      score -= 0.2
      reasons.push("penalize_line_high_group_count")
    }
  }

  if (facetCard > 12 && caps.supportsFacet) {
    score -= 0.35
    reasons.push("penalize_high_facet_count")
  }

  // Large dataset handling
  if (rows > 50_000) {
    if (caps.structure === "scatter_raw") {
      score -= 0.3
      reasons.push("penalize_raw_scatter_large_rows")
    }
    if (caps.structure === "scatter_agg" || caps.structure === "matrix_density") {
      score += 0.2
      reasons.push("boost_aggregated_density_large_rows")
    }
  }

  // Niche chart penalty
  if (["chord", "sankey", "dendrogram", "radar", "polar_area", "population_pyramid"].includes(caps.id)) {
    score -= 0.35
    reasons.push("penalize_niche_chart")
  }

  // Special-case boosts (only meaningful in xy/grouped)
  if (stage !== "xOnly") {
    if (caps.id === "geo_map" && detectGeo(fp)) {
      score += 2.0
      reasons.push("boost_geo_match")
    }
    if (caps.id === "candlestick" && detectOHLC(fp) && xType === "datetime") {
      score += 1.5
      reasons.push("boost_ohlc_match")
    }
    if (caps.id === "volcano_plot" && detectVolcano(fp)) {
      score += 1.5
      reasons.push("boost_volcano_match")
    }
  }

  // Fingerprint suggestions small boost
  const suggested = fp?.suggestions?.plotTypes ?? []
  if (suggested.includes(caps.id)) {
    score += 0.1
    reasons.push("fingerprint_suggestion_match")
  }

  // Clamp
  if (score < 0) score = 0

  return { score, reasons }
}

function selectWithStructureDiversity(candidates: Candidate[], max: number): Candidate[] {
  // Stronger than family diversity: avoid two charts with identical structure unless necessary.
  const picked: Candidate[] = []
  const usedStructures = new Map<ChartStructure, number>()
  const usedFamilies = new Map<ChartFamily, number>()

  for (const c of candidates) {
    if (picked.length >= max) break

    const sCount = usedStructures.get(c.caps.structure) ?? 0
    const fCount = usedFamilies.get(c.caps.family) ?? 0

    // Default caps:
    // - max 1 per structure
    // - max 2 per family
    let structureCap = 1
    let familyCap = 2

    // Allow both line and grouped bar sometimes, but still avoid duplicates within the same structure.
    // Keep structure cap strict.
    if (sCount >= structureCap) continue
    if (fCount >= familyCap) continue

    picked.push(c)
    usedStructures.set(c.caps.structure, sCount + 1)
    usedFamilies.set(c.caps.family, fCount + 1)
  }

  // Fill if we did not reach max (ignore structure constraint, still keep family cap loose)
  if (picked.length < max) {
    for (const c of candidates) {
      if (picked.length >= max) break
      if (picked.some(p => p.id === c.id)) continue
      picked.push(c)
    }
  }

  return picked.slice(0, max)
}

export function recommendCharts(
  fp: Fingerprint | null,
  roles: RoleState,
  allChartIds: string[] = ALL_CHART_IDS
): RecommendResult {
  const stage = stageOf(roles)

  // No recommendations before any roles
  if (stage === "preRoles") {
    return {
      stage,
      recommendedIds: [],
      reasonsByChartId: {},
      hint: "Pick X and Y to see tailored chart recommendations.",
    }
  }

  const yArr = asArrayY(roles.y)
  const yCount = yArr.length
  const hasSeries = !!roles.series
  const hasFacet = !!roles.facet

  const xType = inferType(fp, roles.x ?? null)

  // Infer Y type: for multiY, choose the majority type with a numeric bias
  let yType: ColumnType = "unknown"
  if (stage !== "xOnly") {
    const yTypes = yArr.map(y => inferType(fp, y))
    const counts: Record<ColumnType, number> = { numeric: 0, categorical: 0, datetime: 0, unknown: 0 }
    for (const t of yTypes) counts[t] += 1
    // Prefer numeric if any numeric exists
    if (counts.numeric > 0) yType = "numeric"
    else {
      // otherwise pick max count
      const best = (Object.keys(counts) as ColumnType[]).sort((a, b) => counts[b] - counts[a])[0]
      yType = best ?? "unknown"
    }
  }

  const seriesCard = hasSeries ? (cardinality(fp, roles.series!) ?? 0) : 0
  const facetCard = hasFacet ? (cardinality(fp, roles.facet!) ?? 0) : 0

  const candidates: Candidate[] = []

  for (const chartId of allChartIds) {
    const caps = getCaps(chartId)

    // Hard exclusions
    if (caps.excludeFromRecommendations) continue
    if (EXCLUDED_RECOMMENDATION_CHARTS.has(chartId)) continue

    const { ok } = isCompatibleByStageAndTypes(
      caps,
      stage,
      xType,
      yType,
      yCount,
      hasSeries,
      hasFacet
    )
    if (!ok) continue

    const { score, reasons } = scoreCandidate({
      fp,
      roles,
      stage,
      caps,
      xType,
      yType,
      yCount,
      seriesCard,
      facetCard,
    })

    if (score <= 0) continue

    candidates.push({ id: chartId, score, reasons, caps })
  }

  // Special-case overrides: promote these to the top if detected and compatible
  // This does not bypass exclusions or compatibility.
  function boostIfPresent(id: string, amount: number) {
    const c = candidates.find(x => x.id === id)
    if (c) {
      c.score += amount
      c.reasons.push(`override_boost_${id}`)
    }
  }

  if (stage !== "xOnly") {
    if (detectGeo(fp)) boostIfPresent("geo_map", 3.0)
    if (detectOHLC(fp) && xType === "datetime") boostIfPresent("candlestick", 2.0)
    if (detectVolcano(fp)) boostIfPresent("volcano_plot", 2.0)
  } else {
    // xOnly stage: boost geo_map if geo detection passed
    if (detectGeo(fp)) boostIfPresent("geo_map", 3.0)
  }

  // If nothing found, fall back to safe defaults that also respect exclusions
  if (candidates.length === 0) {
    let fallback: string[]
    if (stage === "xOnly") {
      if (xType === "categorical") {
        // Fallback for categorical X in xOnly
        const xCardinality = cardinality(fp, roles.x ?? null)
        const hasGeo = detectGeo(fp)
        const lowCardinality = xCardinality !== null && xCardinality <= 8
        
        if (hasGeo) {
          // Include geo_map if geo detection passed
          fallback = ["geo_map", "bar_vertical_sorted", "treemap", "bar_vertical"]
          if (lowCardinality) {
            fallback.push("donut")
          }
        } else {
          // No geo: use category frequency charts
          fallback = ["bar_vertical_sorted", "treemap", "bar_vertical"]
          if (lowCardinality) {
            fallback.push("donut")
          }
        }
      } else {
        // Default xOnly fallback for non-categorical X
        fallback = ["histogram", "density_curve_kde", "bar_vertical", "boxplot"]
      }
    } else if (xType === "numeric" && yType === "categorical") {
      // Special fallback for numericX+categoricalY pattern
      fallback = ["violin", "boxplot", "ridgeline", "scatter"]
    } else {
      fallback = ["bar_vertical", "line", "scatter", "boxplot"]
    }

    const recommendedIds = fallback.filter(id => !EXCLUDED_RECOMMENDATION_CHARTS.has(id)).slice(0, 4)
    const reasonsByChartId: Record<string, string[]> = {}
    for (const id of recommendedIds) reasonsByChartId[id] = ["fallback_default"]
    return { stage, recommendedIds, reasonsByChartId }
  }

  // Sort by score desc, stable tie-break by id
  candidates.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score
    return a.id.localeCompare(b.id)
  })

  // Pattern-aware selection mix for numericX+categoricalY
  let picked: Candidate[]
  if (xType === "numeric" && yType === "categorical" && stage !== "xOnly") {
    // Compute heuristics for aggregated vs raw distribution
    const yCardinality = cardinality(fp, roles.y ? (Array.isArray(roles.y) ? roles.y[0] : roles.y) : null) ?? 0
    const rowsPerCategory = (fp?.rows ?? 0) > 0 && yCardinality > 0 ? (fp?.rows ?? 0) / Math.max(1, yCardinality) : Infinity
    const looksAggregated = rowsPerCategory <= 1.5

    const seedPicks: Candidate[] = []
    const usedIds = new Set<string>()

    // 1. Pick 1 from [violin, boxplot] (prefer violin slightly)
    const distributionCharts = candidates.filter(c => c.id === "violin" || c.id === "boxplot")
    if (distributionCharts.length > 0) {
      const best = distributionCharts[0] // Already sorted by score, violin should rank higher
      seedPicks.push(best)
      usedIds.add(best.id)
    }

    // 2. Then ridgeline if available
    const ridgeline = candidates.find(c => c.id === "ridgeline" && !usedIds.has(c.id))
    if (ridgeline) {
      seedPicks.push(ridgeline)
      usedIds.add(ridgeline.id)
    }

    // 3. Then scatter
    const scatter = candidates.find(c => c.id === "scatter" && !usedIds.has(c.id))
    if (scatter) {
      seedPicks.push(scatter)
      usedIds.add(scatter.id)
    }

    // 4. If aggregated, pick 1 from [diverging_bar_single_value_per_row, bullet_graph, bar_horizontal_stacked]
    if (looksAggregated) {
      const aggregatedCharts = candidates.filter(
        c => (c.id === "diverging_bar_single_value_per_row" || c.id === "bullet_graph" || c.id === "bar_horizontal_stacked") && !usedIds.has(c.id)
      )
      if (aggregatedCharts.length > 0) {
        seedPicks.push(aggregatedCharts[0]) // Already sorted by score
        usedIds.add(aggregatedCharts[0].id)
      }
    }

    // Fill remaining slots (to 4) with diversity selection over remaining candidates
    // Exclude heatmap for small/medium datasets (unless it's huge dataset)
    const xCardinality = cardinality(fp, roles.x ?? null) ?? 0
    const isHugeDataset = (fp?.rows ?? 0) > 50_000 || xCardinality > 1000
    const remaining = candidates.filter(c => {
      if (usedIds.has(c.id)) return false
      // Exclude heatmap unless dataset is huge
      if (c.id === "heatmap" && !isHugeDataset) return false
      return true
    })
    const diversityPicks = selectWithStructureDiversity(remaining, 4 - seedPicks.length)
    picked = [...seedPicks, ...diversityPicks].slice(0, 4)
  } else {
    // Default: Pick top 4 with structure diversity
    picked = selectWithStructureDiversity(candidates, 4)
  }

  const recommendedIds = picked.map(p => p.id)
  const reasonsByChartId: Record<string, string[]> = {}
  for (const p of picked) reasonsByChartId[p.id] = p.reasons

  return { stage, recommendedIds, reasonsByChartId }
}
